import {
  FOC_TOUR_LABEL_WS,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import { FOC_REQUIRED_DISPOSITION } from '../orgWorkspaceTour/orgWorkspaceTourSteps';

export const FOC_WS_TOUR_PARAM = 'focWsTour';

/** Seat used for the FOC 13.1 workspace walkthrough (forms + tasks, no SITL map). */
export const FOC_WS_TOUR_SEAT_SLUG = 'planning-section-chief';

/** Hub Product Tours entry — bootstrap ensures FOC Demo Planning P, then opens the PSC seat. */
export const FOC_WS_TOUR_HREF = `/hub?${FOC_WS_TOUR_PARAM}=1`;

export function focWsTourSeatHref(incidentId: string): string {
  return `/iap/${incidentId}/seat/${FOC_WS_TOUR_SEAT_SLUG}?${FOC_WS_TOUR_PARAM}=1`;
}

export type FocWsTourStepId =
  | 'sectionIntro'
  | 'ws001'
  | 'ws001return'
  | 'ws001a'
  | 'ws001aTasks'
  | 'ws001aCop'
  | 'ws001b'
  | 'focFeedback';

export type FocWsTourStepDef = {
  id: FocWsTourStepId;
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  bullets?: string[];
  tour?: string;
};

type RequirementInput = Omit<FocWsTourStepDef, 'title' | 'requirementId'> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): FocWsTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

export const FOC_WS_TOUR_STEPS: FocWsTourStepDef[] = [
  {
    id: 'sectionIntro',
    title: '13.1 Role-Based Incident Workspaces',
    description:
      'PRATUS provides persistent, incident-specific role workspaces for Command Staff, General Staff, section chiefs, unit leaders, and other authorized personnel. Planning-P meeting screens coordinate work; they do not replace the seat workspaces where users create, revise, submit, review, approve, and manage operational products.',
  },
  requirement({
    id: 'ws001',
    requirementId: 'FOC-WS-001',
    name: 'Persistent Role-Based Workspaces',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-root',
    description:
      'This Planning Section Chief workspace is a persistent, incident-specific role workspace. Authorized incident personnel open their seats from Position Workspaces or Planning-P “Open position workspace” links. Meetings coordinate the cycle; product work happens here.',
  }),
  {
    id: 'ws001return',
    title: 'FOC-WS-001 — Meetings link into workspaces',
    tour: 'position-workspace-return',
    description:
      'Return to Planning-P (honoring returnPhase when opened from a meeting) keeps facilitation and product work connected. Planning-P phases that map to a seat expose “Open position workspace” so users leave the meeting screen for the workspace where products are authored and managed.',
  },
  requirement({
    id: 'ws001a',
    requirementId: 'FOC-WS-001a',
    name: 'Common Workspace Functions — Forms',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-forms',
    description:
      'Role-based workspaces provide assigned deliverables and related forms for the seat — current and next-period products open from the Forms section so users create, revise, and export operational products without leaving the workspace.',
  }),
  {
    id: 'ws001aTasks',
    title: 'FOC-WS-001a — Tasks and work status',
    tour: 'position-workspace-tasks',
    description:
      'Tasks show work status and tasking for the position. Due dates, blockers, dependencies, review/approval, audit history, and notifications are available through task status, linked Actions, form signatures/versioning, Activity Logs, and incident Notifications — not as separate seat-home columns in this walkthrough.',
  },
  {
    id: 'ws001aCop',
    title: 'FOC-WS-001a — Map / COP context',
    description:
      'Incident map and COP context are role-specific. Situation Unit Leader and Hub COP product tours cover COP toolkit surfaces; this Planning Section Chief workspace focuses forms and tasks for planning deliverables.',
  },
  requirement({
    id: 'ws001b',
    requirementId: 'FOC-WS-001b',
    name: 'Cross-Functional Visibility',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Authorized users can view status of work products and dependencies needed to coordinate the incident — via Position Workspaces, Planning-P form status, and related products — without receiving access to restricted source content, financial information, sensitive information, or other information outside their authorization.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_WS),
  },
];

export function focWsTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focWsTourStepIds(): FocWsTourStepId[] {
  return FOC_WS_TOUR_STEPS.map((step) => step.id);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function focWsTourPopoverHtml(
  step: Pick<FocWsTourStepDef, 'disposition' | 'description' | 'bullets'>
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}
